import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
// import { DataFrame, GeoDataFrame, Dataset } from '@oceanum/datamesh-js-core';
import { Dataset } from '@oceanum/datamesh';
import { bboxClip, bboxPolygon, pointsWithinPolygon } from '@turf/turf';

import { useDataLoading } from 'framework';

dayjs.extend(duration);

const dataLoading = useDataLoading();

const array_indexer = (array, indexer, indexdim, idim = 0) => {
  if (idim == indexdim) {
    const a = [];
    indexer.map((i) => {
      a.push(array[i]);
    });
    return a;
  } else {
    return array.map((v) => array_indexer(v, indexer, indexdim, idim + 1));
  }
};

//Merge the datasource and query filters
const make_geofilter = (dsfilter, qfilter) => {
  if (!qfilter) return dsfilter;
  if (!dsfilter) return qfilter;
  const qf = qfilter.type == 'feature' ? qfilter.geom : bboxPolygon(qfilter);
  if (dsfilter.type == 'bbox') {
    return bboxClip(qf, dsfilter.geom);
  } else if (dsfilter.geom.type == 'Polygon') {
    if (qfilter.geom.type == 'Polygon') {
      return intersect(qf, dsfilter.geom);
    } else {
      return pointsWithinPolygon(qfilter.geom, dsfilter.geom);
    }
  } else {
    return dsfilter;
  }
};

//datamesh-js expects times as native JS dates or strings
const make_timefilter = (times, timeSelect) => {
  if (timeSelect && timeSelect.mode == 'nearest') {
    const tol = timeSelect.tolerance || dayjs.duration(60, 's');
    return {
      type: 'range',
      times: [
        dayjs(times[0]).subtract(tol).toDate(),
        dayjs(times[times.length - 1])
          .add(tol)
          .toDate(),
      ],
    };
  } else if (timeSelect && timeSelect.mode == 'exact') {
    return { times: [times[0].toDate()], type: 'series' };
  } else {
    return { type: 'range', times: [times[0].toDate(), times[1].toDate()] };
  }
};

//Squeeze singleton dimensions
export const squeeze_dataset = (data) => {
  Object.keys(data.variables).forEach((k) => {
    let dimensions = [...data.variables[k].dimensions];
    if (dimensions.length > 1) {
      data.variables[k].dimensions.forEach((d) => {
        if (data.dimensions[d] == 1) {
          data.variables[k].data = data.variables[k].data[0];
          dimensions = dimensions.filter((dim) => dim != d);
        }
      });
      data.variables[k].dimensions = dimensions;
    }
  });
  return data;
};

export class DataWrapper {
  constructor(id, datasource, oceanql = {}, refresh = false) {
    this.id = id;
    this.datasource = datasource;
    this.oceanql = oceanql;
    this.refresh = refresh;
  }
  static async inline(id, data) {
    let datasource = null;
    if ((data.features && data.features.length) || data.geometry) {
      datasource = await Dataset.fromGeojson(data);
    } else if (data.variables) {
      const { coordkeys, ..._datasource } = data;
      datasource = await Dataset.init(_datasource, coordkeys);
    }
    return new DataWrapper(id, datasource, {}, true);
  }
  async query(query, squeeze) {
    if (!this.datasource) return null;
    const tname = this.datasource.coordkeys.t;
    const qt = tname && query?.timefilter;

    dataLoading.start(this.id);
    const data = this.datasource;
    const new_variables = {};
    const dimensions = { ...data.dimensions };
    if (qt) {
      const tcoord = await data.variables[tname].get();
      let ind = Array.from(tcoord.keys());
      if (qt.type == 'nearest') {
        const t0 = dayjs(qt.times[0]).unix();
        const tdiff = new Float64Array(tcoord.length);
        tcoord.forEach((t, i) => (tdiff[i] = Math.abs(t - t0)));
        ind.sort((a, b) => (tdiff[a] < tdiff[b] ? -1 : 1));
        if (qt.groupby) {
          const group = await data.variables[qt.groupby].get();
          const groupind = ind.reduce((g, i) => {
            let v = group[i];
            if (!g[v]) g[v] = i;
            return g;
          }, {});
          ind = Object.values(groupind);
        } else {
          ind = ind.slice(0, 1);
        }
        const tol =
          (qt.tolerance && dayjs.duration(qt.tolerance).as('seconds')) ||
          tcoord[1] - tcoord[0];
        ind = ind.filter((i) => Math.abs(tcoord[i] - t0) <= tol);
      } else if (qt.type == 'range') {
        const t0 = dayjs(qt.times[0]).unix();
        const t1 = dayjs(qt.times[1]).unix();
        const indexes = tcoord.map((_, i) => i);
        ind = indexes.filter((i) => t0 <= tcoord[i] && tcoord[i] <= t1);
      }
      if (ind.length == 0) {
        return null;
      }
      //Sort by index
      ind.sort((a, b) => a - b);
      const i0 = ind[0];
      const i1 = ind[ind.length - 1] + 1;
      //Sort by time
      ind.sort((a, b) => tcoord[a] - tcoord[b]);
      for (const k of query.variables || Object.keys(data.variables)) {
        if (!data.variables[k]) {
          console.warn('Variable ' + k + ' not found');
          continue;
        }
        new_variables[k] = {
          dimensions: data.variables[k].dimensions,
          attributes: data.variables[k].attributes,
        };
        const tdim = data.variables[tname].dimensions[0];
        if (data.variables[k].dimensions) {
          const tdind = data.variables[k].dimensions.indexOf(tdim);
          if (tdind < 0) {
            new_variables[k].data = await data.variables[k].get();
          } else {
            const selector = data.variables[k].dimensions.map((d) => null);
            selector[tdind] = { start: i0, stop: i1 };
            const _var = await data.variables[k].get(selector);
            const _ind = ind.map((i) => i - i0);
            new_variables[k].data = array_indexer(_var, _ind, tdind, 0);
          }
        }
      }
      dimensions[tname] = ind.length;
    } else {
      for (const k in data.variables) {
        new_variables[k] = {
          dimensions: data.variables[k].dimensions,
          attributes: data.variables[k].attributes,
          data: await data.variables[k].get(),
        };
      }
    }
    this.refresh = false;
    dataLoading.stop(this.id);
    if (squeeze) {
      return squeeze_dataset({
        attributes: data.attributes,
        variables: new_variables,
        dimensions,
        coordkeys: this.datasource.coordkeys,
      });
    } else {
      return {
        attributes: data.attributes,
        variables: new_variables,
        dimensions,
        coordkeys: this.datasource.coordkeys,
      };
    }
  }
}
